import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';
import PatientRegistration from './PatientRegistration';
import AppointmentScheduling from './AppointmentScheduling';
import DoctorAppointmentDetailModal from '../components/DoctorAppointmentDetailModal';
import PatientDetailModal from '../components/PatientDetailModal';
import SearchBox, { appointmentPatientOptions, loadPatientOptions } from '../components/SearchBox';
import { vitalLines } from '../utils/patientVitals';
import { loadClinicLogo } from '../utils/clinicLogo';
import { downloadPrescriptionPdf } from '../utils/prescriptionPdf';
import RoomCalendar from '../components/RoomCalendar';

const TAX_TYPES = ['GST', 'VAT', 'Service Tax', 'Custom Tax', 'Other'];

function taxesFor(taxConfigs, category) {
  return taxConfigs.filter((tax) => {
    const applies = tax.APPLICABLE_TO || [];
    return applies.includes(category) || applies.includes('All');
  });
}

function taxChoices(taxConfigs, category) {
  const saved = taxesFor(taxConfigs, category);
  if (saved.length > 0) return saved;
  return TAX_TYPES.map((type) => ({
    _id: `type:${type}`,
    TAX_NAME: type,
    TAX_TYPE: type,
    TAX_RATE: null
  }));
}

const ReceptionDashboard = () => {
  // Active section state
  const [activeSection, setActiveSection] = useState(null);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // User permissions state
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // State for patient search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // State for viewing patient details
  const [selectedPatient, setSelectedPatient] = useState(null);

  // State for invoice generation
  const [invoiceAppointments, setInvoiceAppointments] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [selectedInvoiceAppointment, setSelectedInvoiceAppointment] = useState(null);
  const [invoiceFormData, setInvoiceFormData] = useState({
    includeConsultation: false,
    includeMedicines: false,
    includeLabTests: false,
    includeProcedures: false,
    consultationTaxId: '',
    consultationTaxRate: 0,
    consultationDiscount: 0,
    medicinesTaxId: '',
    medicinesTaxRate: 0,
    medicinesDiscount: 0,
    labTestsTaxId: '',
    labTestsTaxRate: 0,
    labTestsDiscount: 0,
    proceduresTaxId: '',
    proceduresTaxRate: 0,
    proceduresDiscount: 0,
    notes: ''
  });
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [taxConfigs, setTaxConfigs] = useState([]);

  // State for Print Prescription
  const [prescriptionAppointment, setPrescriptionAppointment] = useState(null);
  const [prescriptionLoading, setPrescriptionLoading] = useState(false);
  const [appointmentSearchTerm, setAppointmentSearchTerm] = useState('');
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Helper function to check if user has permission for a gadget
  const hasPermission = (gadgetName, permissionType = 'VIEW') => {
    const permission = userPermissions[gadgetName];
    return permission && permission[permissionType] === true;
  };

  // Function to fetch user permissions
  const fetchUserPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPermissionsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/gadgetPermissions/my-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const permissions = await response.json();
        setUserPermissions(permissions);
        console.log('Reception Dashboard - User permissions:', permissions);
      } else {
        console.error('Failed to fetch user permissions');
        setUserPermissions({});
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setUserPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Function to handle patient search
  const handleSearch = async (term) => {
    const query = typeof term === 'string' ? term : searchTerm;
    setSearchLoading(true);
    setSearchError('');
    setSearchResults([]);

    const token = localStorage.getItem('token');
    if (!token) {
      setSearchError('Authentication token not found. Please log in again.');
      setSearchLoading(false);
      return;
    }

    if (!query.trim()) {
      setSearchError('Please enter a search term.');
      setSearchLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/patients/search?search=${encodeURIComponent(query)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();

      if (response.ok) {
        setSearchResults(Array.isArray(data) ? data : []);
      } else {
        setSearchError(data.message || 'Failed to search patients.');
      }
    } catch (err) {
      setSearchError(`Network error: ${err.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleViewPatientDetails = (patient) => {
    setSelectedPatient(patient);
  };

  const handleClosePatientDetails = () => {
    setSelectedPatient(null);
  };

  const handleViewDetails = (appointment) => {
    console.log('Opening appointment details for:', appointment);
    console.log('Appointment data structure:', {
      _id: appointment._id,
      APPOINTMENT_ID: appointment.APPOINTMENT_ID,
      PATIENT_ID: appointment.PATIENT_ID,
      DOCTOR_ID: appointment.DOCTOR_ID,
      DR_MEDICINES: appointment.DR_MEDICINES,
      DR_PROCEDURES: appointment.DR_PROCEDURES,
      DR_LAB_TESTS: appointment.DR_LAB_TESTS
    });
    setSelectedAppointment(appointment);
  };

  const handleCloseDetails = () => {
    setSelectedAppointment(null);
  };

  const handleSaveAppointment = () => {
    fetchAppointments();
  };

  // Invoice generation functions
  const fetchInvoiceAppointments = async () => {
    setInvoiceLoading(true);
    setInvoiceError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setInvoiceError('Authentication token not found. Please log in again.');
      setInvoiceLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/appointments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        const appointmentsData = Array.isArray(data) ? data : [];
        console.log('All appointments fetched:', appointmentsData.length);
        console.log('Appointment statuses:', appointmentsData.map(apt => apt.APPOINTMENT_STATUS));
        
        // Filter for completed appointments only
        const invoiceableAppointments = appointmentsData.filter(apt => 
          apt.APPOINTMENT_STATUS === 'Completed'
        );
        console.log('Invoiceable appointments found:', invoiceableAppointments.length);
        console.log('Invoiceable appointments:', invoiceableAppointments.map(apt => ({
          id: apt._id,
          patient: apt.PATIENT_ID?.PATIENT_NAME,
          doctor: apt.DOCTOR_ID?.DOCTOR_NAME,
          status: apt.APPOINTMENT_STATUS,
          fee: apt.APPOINTMENT_FEE,
          medicines: apt.DR_MEDICINES?.length || 0,
          procedures: apt.DR_PROCEDURES?.length || 0,
          labTests: apt.DR_LAB_TESTS?.length || 0
        })));
        setInvoiceAppointments(invoiceableAppointments);
      } else {
        setInvoiceError(data.message || 'Failed to fetch appointments.');
      }
    } catch (err) {
      setInvoiceError(`Network error: ${err.message}`);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleInvoiceFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInvoiceFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGenerateInvoice = async () => {
    if (!selectedInvoiceAppointment) {
      setInvoiceError('Please select an appointment to generate invoice.');
      return;
    }

    if (!invoiceFormData.includeConsultation && !invoiceFormData.includeMedicines && 
        !invoiceFormData.includeLabTests && !invoiceFormData.includeProcedures) {
      setInvoiceError('Please select at least one item to include in the invoice.');
      return;
    }

    setInvoiceGenerating(true);
    setInvoiceError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setInvoiceError('Authentication token not found. Please log in again.');
      setInvoiceGenerating(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/invoices/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          APPOINTMENT_ID: selectedInvoiceAppointment._id,
          ...invoiceFormData
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setGeneratedInvoice(data.invoice);
        setInvoiceFormData({
          includeConsultation: false,
          includeMedicines: false,
          includeLabTests: false,
          includeProcedures: false,
          consultationTaxId: '',
          consultationTaxRate: 0,
          consultationDiscount: 0,
          medicinesTaxId: '',
          medicinesTaxRate: 0,
          medicinesDiscount: 0,
          labTestsTaxId: '',
          labTestsTaxRate: 0,
          labTestsDiscount: 0,
          proceduresTaxId: '',
          proceduresTaxRate: 0,
          proceduresDiscount: 0,
          notes: ''
        });
        setSelectedInvoiceAppointment(null);
        alert('Invoice generated successfully!');
      } else {
        setInvoiceError(data.message || 'Failed to generate invoice.');
      }
    } catch (err) {
      setInvoiceError(`Network error: ${err.message}`);
    } finally {
      setInvoiceGenerating(false);
    }
  };

  // Function to fetch appointments
  const fetchAppointments = async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/appointments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        const appointmentsData = Array.isArray(data) ? data : [];
        setAppointments(appointmentsData);
        
        // Debug: Log appointments data
        console.log('Fetched appointments:', appointmentsData);
        console.log('Completed appointments with diagnosis:', 
          appointmentsData.filter(apt => apt.APPOINTMENT_STATUS === 'Completed' && apt.DIAGNOSIS));
        
        // Show all appointment details
        appointmentsData.forEach((apt, index) => {
          console.log(`Appointment ${index + 1}:`, {
            id: apt._id,
            status: apt.APPOINTMENT_STATUS,
            diagnosis: apt.DIAGNOSIS,
            patient: apt.PATIENT_ID?.PATIENT_NAME,
            date: apt.APPOINTMENT_DATE
          });
        });
      } else {
        setError(data.message || 'Failed to fetch appointments.');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter appointments for prescription based on search term
  const getFilteredAppointments = () => {
    // TEMPORARY: Include all appointments for testing (remove DIAGNOSIS requirement)
    const completedAppointments = appointments.filter(apt => 
      apt.APPOINTMENT_STATUS === 'Completed' // Removed && apt.DIAGNOSIS for testing
    );

    console.log('All appointments:', appointments);
    console.log('Completed appointments with diagnosis:', completedAppointments);

    if (!appointmentSearchTerm.trim()) {
      return completedAppointments;
    }

    const searchLower = appointmentSearchTerm.toLowerCase();
    const filtered = completedAppointments.filter(appointment => {
      const patientName = appointment.PATIENT_ID?.PATIENT_NAME?.toLowerCase() || '';
      const patientId = appointment.PATIENT_ID?.PATIENT_ID?.toLowerCase() || '';
      const contactNumber = appointment.PATIENT_ID?.PATIENT_PHONE?.toLowerCase() || '';
      
      return patientName.includes(searchLower) || 
             patientId.includes(searchLower) || 
             contactNumber.includes(searchLower);
    });

    console.log('Searching for:', appointmentSearchTerm);
    console.log('Patient data:', completedAppointments.map(apt => ({
      name: apt.PATIENT_ID?.PATIENT_NAME,
      id: apt.PATIENT_ID?.PATIENT_ID,
      phone: apt.PATIENT_ID?.PATIENT_PHONE
    })));
    console.log('Filtered results:', filtered);

    return filtered;
  };

  // Generate PDF prescription
  const generatePrescriptionPDF = async () => {
    if (!prescriptionAppointment) {
      alert('Please select an appointment to generate prescription.');
      return;
    }

    setPrescriptionLoading(true);
    
    try {
      const prescriptionLogo = await loadClinicLogo();
      await downloadPrescriptionPdf({ appointment: prescriptionAppointment, logo: prescriptionLogo });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setPrescriptionLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
    fetchAppointments();
  }, []);

  // Fetch invoice appointments when invoice section is activated
  useEffect(() => {
    if (activeSection === 'invoiceGeneration') {
      fetchInvoiceAppointments();
      const token = localStorage.getItem('token');
      if (!token) return;
      fetch(`${API_BASE_URL}/tax/current-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then((response) => response.ok ? response.json() : []).then((data) => {
        setTaxConfigs(Array.isArray(data) ? data : []);
      }).catch(() => setTaxConfigs([]));
    }
  }, [activeSection]);

  useEffect(() => {
    setInvoiceFormData((current) => {
      const next = { ...current };
      let changed = false;
      const apply = (included, idKey, rateKey, category) => {
        if (!included || next[idKey]) return;
        const matches = taxesFor(taxConfigs, category);
        if (matches.length !== 1 || matches[0].TAX_RATE == null) return;
        next[idKey] = matches[0]._id;
        next[rateKey] = matches[0].TAX_RATE;
        changed = true;
      };
      apply(current.includeConsultation, 'consultationTaxId', 'consultationTaxRate', 'Consultation');
      apply(current.includeMedicines, 'medicinesTaxId', 'medicinesTaxRate', 'Medicine');
      apply(current.includeLabTests, 'labTestsTaxId', 'labTestsTaxRate', 'LabTest');
      apply(current.includeProcedures, 'proceduresTaxId', 'proceduresTaxRate', 'Procedure');
      return changed ? next : current;
    });
  }, [taxConfigs, invoiceFormData.includeConsultation, invoiceFormData.includeMedicines, invoiceFormData.includeLabTests, invoiceFormData.includeProcedures]);

  const chooseTax = (idKey, rateKey, taxId) => {
    const tax = taxConfigs.find((item) => item._id === taxId);
    setInvoiceFormData((current) => ({
      ...current,
      [idKey]: taxId,
      [rateKey]: tax ? tax.TAX_RATE : 0
    }));
  };

  // Trigger search when searchTrigger changes
  useEffect(() => {
    if (searchTrigger > 0) {
      console.log('Search triggered, appointments:', appointments);
      console.log('Search term:', appointmentSearchTerm);
      console.log('Filtered results:', getFilteredAppointments());
    }
  }, [searchTrigger, appointmentSearchTerm, appointments]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem' }}>
      <h2>Reception Dashboard</h2>
      
      {/* Dashboard Gadgets/Buttons */}
      {permissionsLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
          Loading dashboard permissions...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        
        {/* Patient Registration Gadget */}
        {hasPermission('Patient Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>👤 Patient Registration</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Register new patients in the system
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'patientRegistration' ? null : 'patientRegistration')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'patientRegistration' ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'patientRegistration' ? 'Close Registration' : 'Register Patient'}
            </button>
          </div>
        )}

        {/* Appointment Scheduling Gadget */}
        {hasPermission('Appointment Scheduling') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📅 Schedule Appointment</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Schedule appointments for patients
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'appointmentScheduling' ? null : 'appointmentScheduling')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'appointmentScheduling' ? '#dc3545' : '#ffc107',
                color: activeSection === 'appointmentScheduling' ? 'white' : '#212529',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'appointmentScheduling' ? 'Close Scheduling' : 'Schedule Appointment'}
            </button>
          </div>
        )}

        {/* Patient Search Gadget */}
        {hasPermission('Patient Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🔍 Patient Search</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Search and view patient information
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'patientSearch' ? null : 'patientSearch')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'patientSearch' ? '#dc3545' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'patientSearch' ? 'Close Search' : 'Search Patients'}
            </button>
          </div>
        )}

        {/* Appointments Overview Gadget */}
        {hasPermission('Appointment Scheduling') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📋 Appointments Overview</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              View all scheduled appointments
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'appointmentsOverview' ? null : 'appointmentsOverview')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'appointmentsOverview' ? '#dc3545' : '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'appointmentsOverview' ? 'Close Overview' : 'View Appointments'}
            </button>
          </div>
        )}

        {hasPermission('Room Calendar') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🚪 Room calendar</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              See which room is booked and the room details
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'roomCalendar' ? null : 'roomCalendar')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'roomCalendar' ? '#dc3545' : '#0f766e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'roomCalendar' ? 'Close rooms' : 'View rooms'}
            </button>
          </div>
        )}

        {/* Print Prescription Gadget */}
        {hasPermission('Patient Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📄 Print Prescription</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Generate and print prescription PDFs
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'printPrescription' ? null : 'printPrescription')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'printPrescription' ? '#dc3545' : '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'printPrescription' ? 'Close Prescription' : 'Print Prescription'}
            </button>
          </div>
        )}

        {/* Invoice Generation Gadget */}
        {hasPermission('Invoice Generation') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🧾 Generate Invoice</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Generate invoices for completed appointments
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'invoiceGeneration' ? null : 'invoiceGeneration')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'invoiceGeneration' ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'invoiceGeneration' ? 'Close Invoice' : 'Generate Invoice'}
            </button>
          </div>
        )}
        
        {/* No gadgets available message */}
        {!permissionsLoading && !hasPermission('Patient Management') && !hasPermission('Appointment Scheduling') && !hasPermission('Invoice Generation') && !hasPermission('Room Calendar') && (
          <div style={{ 
            gridColumn: '1 / -1', 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#6c757d',
            backgroundColor: '#f8f9fa',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}>
            <h3>No Dashboard Access</h3>
            <p>You don't have permission to access any dashboard gadgets. Please contact your administrator.</p>
          </div>
        )}
      </div>
      )}

      {/* Conditional Sections */}
      {activeSection === 'patientRegistration' && hasPermission('Patient Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Patient Registration</h3>
          <PatientRegistration />
        </div>
      )}

      {activeSection === 'appointmentScheduling' && hasPermission('Appointment Scheduling') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Appointment Scheduling</h3>
          <AppointmentScheduling />
        </div>
      )}

      {activeSection === 'patientSearch' && hasPermission('Patient Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Patient Search</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
            <SearchBox
              placeholder="Search patient by name, ID, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              loadOptions={loadPatientOptions}
              onSelect={(option) => {
                const name = option.item.PATIENT_NAME || '';
                setSearchTerm(name);
                handleSearch(name);
              }}
            />
            <button
              onClick={handleSearch}
              disabled={searchLoading}
              style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          {searchError && <p style={{ color: 'red', marginTop: '10px' }}>Error: {searchError}</p>}
          {searchLoading && <p>Searching for patients...</p>}

          {searchResults.length > 0 && (
            <div>
              <h4>Search Results:</h4>
              {searchResults.map((patient) => (
                <div key={patient.PATIENT_ID} style={{ border: '1px solid #eee', padding: '10px', marginBottom: '5px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{patient.PATIENT_NAME}</strong> - {patient.PATIENT_ID} - {patient.CONTACT_NUMBER}
                  </div>
                  <button
                    onClick={() => handleViewPatientDetails(patient)}
                    style={{ padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          )}
          {searchTerm && !searchLoading && searchResults.length === 0 && !searchError && (
            <p>No patients found matching your search.</p>
          )}
        </div>
      )}

      {activeSection === 'roomCalendar' && hasPermission('Room Calendar') && (
        <RoomCalendar />
      )}

      {activeSection === 'appointmentsOverview' && hasPermission('Appointment Scheduling') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Appointments Overview</h3>
          
          {loading && <p>Loading appointments...</p>}
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}

          {!loading && !error && appointments.length === 0 && (
            <p>No appointments found.</p>
          )}

          {!loading && !error && appointments.length > 0 && (
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Patient</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Doctor</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Time</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appointment) => (
                    <tr key={appointment.APPOINTMENT_ID}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {appointment.PATIENT_ID?.PATIENT_NAME || 'Unknown Patient'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {appointment.DOCTOR_ID?.firstName || 'Unknown Doctor'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {new Date(appointment.APPOINTMENT_DATE).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {appointment.APPOINTMENT_SLOT}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          backgroundColor: appointment.APPOINTMENT_STATUS === 'Scheduled' ? '#d4edda' : '#f8d7da',
                          color: appointment.APPOINTMENT_STATUS === 'Scheduled' ? '#155724' : '#721c24'
                        }}>
                          {appointment.APPOINTMENT_STATUS}
                        </span>
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <button
                          onClick={() => handleViewDetails(appointment)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Print Prescription Section */}
      {activeSection === 'printPrescription' && hasPermission('Patient Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Print Prescription</h3>

          {/* Single Search Interface */}
          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#495057', fontWeight: 'bold' }}>Search Patient Appointments</h4>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
              <SearchBox
                placeholder="Search patient by name, ID, or phone..."
                value={appointmentSearchTerm}
                onChange={(e) => setAppointmentSearchTerm(e.target.value)}
                options={appointmentPatientOptions(appointments)}
                onSelect={(option) => {
                  setAppointmentSearchTerm(option.item);
                  setSearchTrigger((prev) => prev + 1);
                }}
              />
              <button
                onClick={() => {
                  console.log('Search button clicked for:', appointmentSearchTerm);
                  console.log('Current appointments:', appointments);
                  console.log('Filtered appointments:', getFilteredAppointments());
                  setSearchTrigger(prev => prev + 1);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Search
              </button>
            </div>

            {/* Search Results */}
            {appointmentSearchTerm && (
              <div>
                <div style={{ marginBottom: '0.5rem', fontWeight: 'bold', color: '#495057' }}>
                  Search Results ({getFilteredAppointments().length} found):
                </div>
                {getFilteredAppointments().length > 0 ? (
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                    {getFilteredAppointments().map(appointment => (
                      <div
                        key={appointment.APPOINTMENT_ID}
                        onClick={() => setPrescriptionAppointment(appointment)}
                        style={{
                          padding: '0.75rem',
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          backgroundColor: prescriptionAppointment?.APPOINTMENT_ID === appointment.APPOINTMENT_ID ? '#e3f2fd' : 'white',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (prescriptionAppointment?.APPOINTMENT_ID !== appointment.APPOINTMENT_ID) {
                            e.target.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (prescriptionAppointment?.APPOINTMENT_ID !== appointment.APPOINTMENT_ID) {
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                          {appointment.PATIENT_ID?.PATIENT_NAME || 'Unknown Patient'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          ID: {appointment.PATIENT_ID?.PATIENT_ID} | 
                          Phone: {appointment.PATIENT_ID?.PATIENT_PHONE} | 
                          Date: {new Date(appointment.APPOINTMENT_DATE).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem' }}>
                          Status: {appointment.APPOINTMENT_STATUS} | 
                          Diagnosis: {appointment.DIAGNOSIS ? 'Yes' : 'No'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#666', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px' }}>
                    No appointments found matching your search.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected Appointment Preview */}
          {prescriptionAppointment && (
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#e8f5e8', borderRadius: '4px', border: '1px solid #c3e6c3' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50', fontWeight: 'bold' }}>Selected Appointment:</h4>
              <div style={{ fontSize: '0.9rem' }}>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Patient:</strong> {prescriptionAppointment.PATIENT_ID?.PATIENT_NAME || 'Unknown Patient'}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>ID:</strong> {prescriptionAppointment.PATIENT_ID?.PATIENT_ID}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Phone:</strong> {prescriptionAppointment.PATIENT_ID?.PATIENT_PHONE}
                  {vitalLines(prescriptionAppointment.PATIENT_ID?.VITALS).length > 0 && (
                    <span style={{ display: 'block' }}><strong>Vitals:</strong> {vitalLines(prescriptionAppointment.PATIENT_ID?.VITALS).join(' · ')}</span>
                  )}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Date:</strong> {new Date(prescriptionAppointment.APPOINTMENT_DATE).toLocaleDateString()}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Status:</strong> {prescriptionAppointment.APPOINTMENT_STATUS}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Diagnosis:</strong> {prescriptionAppointment.DIAGNOSIS || 'No diagnosis recorded'}
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Medicines:</strong> {prescriptionAppointment.DR_MEDICINES?.length || 0} prescribed
                </div>
                <div>
                  <strong>Procedures:</strong> {prescriptionAppointment.DR_PROCEDURES?.length || 0} recommended
                </div>
              </div>
            </div>
          )}

          {/* Generate PDF Button */}
          <button
            onClick={generatePrescriptionPDF}
            disabled={prescriptionLoading || !prescriptionAppointment}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: prescriptionLoading || !prescriptionAppointment ? '#6c757d' : '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: prescriptionLoading || !prescriptionAppointment ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'background-color 0.3s ease'
            }}
          >
            {prescriptionLoading ? 'Generating PDF...' : 'Generate & Download Prescription PDF'}
          </button>

          {!prescriptionAppointment && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6c757d' }}>
              Please select a completed appointment with diagnosis to generate prescription.
            </p>
          )}
        </div>
      )}

      {/* Invoice Generation Section */}
      {activeSection === 'invoiceGeneration' && hasPermission('Invoice Generation') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Generate Invoice</h3>
          
          {invoiceError && <p style={{ color: 'red' }}>Error: {invoiceError}</p>}
          
          {/* Appointment Selection */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
              <label style={{ fontWeight: 'bold' }}>
                Select Appointment:
              </label>
              <button
                onClick={fetchInvoiceAppointments}
                disabled={invoiceLoading}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                {invoiceLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            {invoiceLoading ? (
              <p>Loading appointments...</p>
            ) : invoiceAppointments.length === 0 ? (
              <div style={{ padding: '1rem', backgroundColor: '#f8d7da', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
                <p style={{ color: '#721c24', margin: '0' }}>
                  No completed appointments found for invoicing. This could be because:
                </p>
                <ul style={{ color: '#721c24', margin: '0.5rem 0 0 1rem', padding: '0' }}>
                  <li>No appointments have been marked as "Completed" yet</li>
                  <li>All appointments are still in "Scheduled" or other status</li>
                </ul>
                <p style={{ color: '#721c24', margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                  Please complete some appointments first by updating their status to "Completed".
                </p>
              </div>
            ) : (
              <select
                value={selectedInvoiceAppointment ? selectedInvoiceAppointment._id : ''}
                onChange={(e) => {
                  const appointment = invoiceAppointments.find(apt => apt._id === e.target.value);
                  setSelectedInvoiceAppointment(appointment);
                }}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">Select an appointment</option>
                {invoiceAppointments.map((appointment) => (
                  <option key={appointment._id} value={appointment._id}>
                    {appointment.PATIENT_ID?.PATIENT_NAME} - {appointment.DOCTOR_ID?.DOCTOR_NAME} - {new Date(appointment.APPOINTMENT_DATE).toLocaleDateString()}
                  </option>
                ))}
              </select>
            )}
          </div>

          {selectedInvoiceAppointment && (
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <h4>Appointment Details:</h4>
              <p><strong>Patient:</strong> {selectedInvoiceAppointment.PATIENT_ID?.PATIENT_NAME}</p>
              <p><strong>Doctor:</strong> {selectedInvoiceAppointment.DOCTOR_ID?.DOCTOR_NAME}</p>
              <p><strong>Date:</strong> {new Date(selectedInvoiceAppointment.APPOINTMENT_DATE).toLocaleDateString()}</p>
              <p><strong>Consultation Fee:</strong> ₹{selectedInvoiceAppointment.APPOINTMENT_FEE || 0}</p>
              <p><strong>Medicines:</strong> {selectedInvoiceAppointment.DR_MEDICINES?.length || 0} items</p>
              <p><strong>Lab Tests:</strong> {selectedInvoiceAppointment.DR_LAB_TESTS?.length || 0} items</p>
              <p><strong>Procedures:</strong> {selectedInvoiceAppointment.DR_PROCEDURES?.length || 0} items</p>
            </div>
          )}

          {/* Invoice Options */}
          <div style={{ marginBottom: '1rem' }}>
            <h4>Include in Invoice:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="includeConsultation"
                  checked={invoiceFormData.includeConsultation}
                  onChange={handleInvoiceFormChange}
                />
                Doctor Consultation (₹{selectedInvoiceAppointment?.APPOINTMENT_FEE || 0})
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="includeMedicines"
                  checked={invoiceFormData.includeMedicines}
                  onChange={handleInvoiceFormChange}
                />
                Medicines ({selectedInvoiceAppointment?.DR_MEDICINES?.length || 0} items)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="includeLabTests"
                  checked={invoiceFormData.includeLabTests}
                  onChange={handleInvoiceFormChange}
                />
                Lab Tests ({selectedInvoiceAppointment?.DR_LAB_TESTS?.length || 0} items)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="includeProcedures"
                  checked={invoiceFormData.includeProcedures}
                  onChange={handleInvoiceFormChange}
                />
                Procedures ({selectedInvoiceAppointment?.DR_PROCEDURES?.length || 0} items)
              </label>
            </div>
          </div>

          {/* Individual Tax and Discount Settings */}
          <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 1rem 0' }}>Tax & Discount Settings</h4>
            
            {/* Doctor Consultation */}
            {invoiceFormData.includeConsultation && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', color: '#007bff' }}>Doctor Consultation</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <TaxChoice
                    taxes={taxChoices(taxConfigs, 'Consultation')}
                    value={invoiceFormData.consultationTaxId}
                    rate={invoiceFormData.consultationTaxRate}
                    onChange={(taxId) => chooseTax('consultationTaxId', 'consultationTaxRate', taxId)}
                  />
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Discount Amount (₹):</label>
                    <input
                      type="number"
                      name="consultationDiscount"
                      value={invoiceFormData.consultationDiscount}
                      onChange={handleInvoiceFormChange}
                      min="0"
                      step="0.01"
                      style={{ width: '100%', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Medicines */}
            {invoiceFormData.includeMedicines && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', color: '#28a745' }}>Medicines</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <TaxChoice
                    taxes={taxChoices(taxConfigs, 'Medicine')}
                    value={invoiceFormData.medicinesTaxId}
                    rate={invoiceFormData.medicinesTaxRate}
                    onChange={(taxId) => chooseTax('medicinesTaxId', 'medicinesTaxRate', taxId)}
                  />
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Discount Amount (₹):</label>
                    <input
                      type="number"
                      name="medicinesDiscount"
                      value={invoiceFormData.medicinesDiscount}
                      onChange={handleInvoiceFormChange}
                      min="0"
                      step="0.01"
                      style={{ width: '100%', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Lab Tests */}
            {invoiceFormData.includeLabTests && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', color: '#ffc107' }}>Lab Tests</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <TaxChoice
                    taxes={taxChoices(taxConfigs, 'LabTest')}
                    value={invoiceFormData.labTestsTaxId}
                    rate={invoiceFormData.labTestsTaxRate}
                    onChange={(taxId) => chooseTax('labTestsTaxId', 'labTestsTaxRate', taxId)}
                  />
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Discount Amount (₹):</label>
                    <input
                      type="number"
                      name="labTestsDiscount"
                      value={invoiceFormData.labTestsDiscount}
                      onChange={handleInvoiceFormChange}
                      min="0"
                      step="0.01"
                      style={{ width: '100%', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Procedures */}
            {invoiceFormData.includeProcedures && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', color: '#dc3545' }}>Procedures</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <TaxChoice
                    taxes={taxChoices(taxConfigs, 'Procedure')}
                    value={invoiceFormData.proceduresTaxId}
                    rate={invoiceFormData.proceduresTaxRate}
                    onChange={(taxId) => chooseTax('proceduresTaxId', 'proceduresTaxRate', taxId)}
                  />
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Discount Amount (₹):</label>
                    <input
                      type="number"
                      name="proceduresDiscount"
                      value={invoiceFormData.proceduresDiscount}
                      onChange={handleInvoiceFormChange}
                      min="0"
                      step="0.01"
                      style={{ width: '100%', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Notes:
            </label>
            <textarea
              name="notes"
              value={invoiceFormData.notes}
              onChange={handleInvoiceFormChange}
              rows="3"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              placeholder="Additional notes for the invoice..."
            />
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerateInvoice}
            disabled={invoiceGenerating || !selectedInvoiceAppointment}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: invoiceGenerating || !selectedInvoiceAppointment ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: invoiceGenerating || !selectedInvoiceAppointment ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            {invoiceGenerating ? 'Generating Invoice...' : 'Generate Invoice'}
          </button>

          {generatedInvoice && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#d4edda', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
              <h4 style={{ color: '#155724', margin: '0 0 0.5rem 0' }}>Invoice Generated Successfully!</h4>
              <p style={{ color: '#155724', margin: '0' }}>
                Invoice ID: {generatedInvoice.INVOICE_ID}
              </p>
              <p style={{ color: '#155724', margin: '0' }}>
                Total Amount: ₹{generatedInvoice.TOTAL_AMOUNT}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {selectedAppointment && (
        <DoctorAppointmentDetailModal
          appointment={selectedAppointment}
          onClose={handleCloseDetails}
          onSave={handleSaveAppointment}
          readOnly={true}
        />
      )}

      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          onClose={handleClosePatientDetails}
        />
      )}
    </div>
  );
};

function TaxChoice({ taxes, value, rate, onChange }) {
  const selected = taxes.find((tax) => tax._id === value);
  const rateKnown = selected ? selected.TAX_RATE != null : true;
  return (
    <div>
      <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Tax:</label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{ width: '100%', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
      >
        <option value="">No tax</option>
        {taxes.map((tax) => (
          <option key={tax._id} value={tax._id}>
            {tax.TAX_RATE == null ? tax.TAX_NAME : `${tax.TAX_NAME} (${tax.TAX_TYPE}) — ${tax.TAX_RATE}%`}
          </option>
        ))}
      </select>
      <p style={{ margin: '0.35rem 0 0', fontSize: '0.85rem', color: '#495057' }}>
        {rateKnown ? `Applied rate: ${Number(rate) || 0}%` : 'No rate saved for this tax yet. Add it under Finance → Tax.'}
      </p>
    </div>
  );
}

export default ReceptionDashboard;